import { yupResolver } from "@hookform/resolvers/yup";
import { Alert, Button, Stack } from "@mui/material";
import { DialogModal, Empty, useConfirmClose } from "@zfp-akademie-a-s/zfp-design";
import { FormTextField } from "@zfp-akademie-a-s/zfp-design/form";
import { CheckCircleIcon, ErrorIcon } from "@zfp-akademie-a-s/zfp-design/icons";
import { useCreateAnalysisIssueReport, useGetAnalysis } from "Infrastructure/ApiZfp/AnalyzerApi";
import { useUserContext } from "Infrastructure/Context/UserContext";
import { Spacing } from "Infrastructure/Themes/Themes";
import { type ResourcePathType, Resources, useResource } from "Infrastructure/Translations/Resources";
import { useContext, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { type ObjectSchema, object, string } from "yup";
import { REPORT_ISSUE_DESCRIPTION_MAX_LENGTH } from "../../constants";
import { AnalysisDetailContext } from "../AnalysisDetailContext";
import { ReportIssueAttachments } from "./ReportIssueAttachments";
import { getClientEnvironment } from "./getClientEnvironment";

type ReportIssueFormType = {
  description: string;
};

const getReportIssueSchema: (t: (resourcePath: ResourcePathType, options?: any) => string) => ObjectSchema<ReportIssueFormType> = (t) => {
  return object({
    description: string()
      .trim()
      .required(t(Resources.Validation.Required))
      .max(REPORT_ISSUE_DESCRIPTION_MAX_LENGTH, t(Resources.Validation.String.Max, { count: REPORT_ISSUE_DESCRIPTION_MAX_LENGTH })),
  }).defined();
};

type Props = {
  readonly open: boolean;
  readonly onClose: () => void;
};

export const ReportIssueModal = ({ open, onClose }: Props) => {
  const { t } = useResource();
  const { analysisId, versionId } = useContext(AnalysisDetailContext);
  const { data: analysis } = useGetAnalysis(analysisId, versionId);
  const { user } = useUserContext();

  const [attachments, setAttachments] = useState<File[]>([]);

  const methods = useForm<ReportIssueFormType>({
    resolver: yupResolver(getReportIssueSchema(t)),
    defaultValues: { description: "" },
  });

  const {
    control,
    handleSubmit,
    reset: resetForm,
    formState: { errors },
  } = methods;

  const { mutate: sendIssueReport, isPending, isSuccess, isError, reset: resetMutation } = useCreateAnalysisIssueReport();

  const description = useWatch({ control, name: "description" });
  const isFormView = !isSuccess && !isError;
  const hasContent = !!description?.trim() || attachments.length > 0;

  const closeModal = () => {
    onClose();
    resetMutation();
  };

  const discardAndClose = () => {
    resetForm();
    setAttachments([]);
    closeModal();
  };

  const { requestClose, confirmCloseDialog } = useConfirmClose({
    isDirty: isFormView && hasContent,
    // Keeps the filled-in form after an error, so reopening the modal lets the user try again.
    onClose: isError ? closeModal : discardAndClose,
    texts: {
      title: t(Resources.Common.ConfirmClose.Title),
      message: t(Resources.Common.ConfirmClose.Text),
      confirmLabel: t(Resources.Common.ConfirmClose.Confirm),
      cancelLabel: t(Resources.Common.ConfirmClose.Cancel),
    },
  });

  const onSubmit = ({ description }: ReportIssueFormType) => {
    sendIssueReport({
      analysisId,
      versionId,
      data: {
        description,
        attachments,
        pageUrl: window.location.href,
        analysisName: analysis?.name ?? "",
        analysisVersion: analysis?.versions.find((version) => version.versionId === versionId)?.version,
        reporterName: user ? `${user.firstName} ${user.lastName}` : "",
        ...getClientEnvironment(),
      },
    });
  };

  const getContent = () => {
    if (isSuccess) {
      return (
        <Empty
          icon={<CheckCircleIcon sx={{ color: "success.main" }} />}
          title={t(Resources.AnalysisDetail.ReportIssue.Sent.Title)}
          text={t(Resources.AnalysisDetail.ReportIssue.Sent.Text)}
        />
      );
    }
    if (isError) {
      return (
        <Empty
          icon={<ErrorIcon sx={{ color: "error.main" }} />}
          title={t(Resources.AnalysisDetail.ReportIssue.Error.Title)}
          text={t(Resources.AnalysisDetail.ReportIssue.Error.Text)}
        />
      );
    }
    return (
      <Stack spacing={Spacing.XXL}>
        <Alert severity="info">{t(Resources.AnalysisDetail.ReportIssue.Hint)}</Alert>
        <FormTextField
          control={control}
          name="description"
          label={t(Resources.AnalysisDetail.ReportIssue.Description)}
          maxLength={REPORT_ISSUE_DESCRIPTION_MAX_LENGTH}
          disabled={isPending}
          placeholder={t(Resources.AnalysisDetail.ReportIssue.DescriptionPlaceholder)}
          multiline
          minRows={6}
          maxRows={10}
          fullWidth
        />
        <ReportIssueAttachments files={attachments} onChange={setAttachments} disabled={isPending} />
      </Stack>
    );
  };

  const getPrimaryAction = () => {
    if (isSuccess) return undefined;
    if (isError)
      return (
        <Button variant="contained" onClick={resetMutation}>
          {t(Resources.AnalysisDetail.ReportIssue.Error.Retry)}
        </Button>
      );
    return (
      <Button variant="contained" type="submit" loading={isPending} disabled={!!errors.description}>
        {t(Resources.AnalysisDetail.ReportIssue.Submit)}
      </Button>
    );
  };

  return (
    <>
      <FormProvider {...methods}>
        <DialogModal
          open={open}
          onClose={requestClose}
          closeLabel={t(Resources.Common.Close)}
          isPending={isPending}
          title={t(Resources.AnalysisDetail.ReportIssue.Title)}
          content={getContent()}
          onSubmit={handleSubmit(onSubmit)}
          primaryAction={getPrimaryAction()}
          size="md"
        />
      </FormProvider>
      {confirmCloseDialog}
    </>
  );
};
